import { useEffect, useLayoutEffect, useRef, type ReactNode } from "react"
import { useLocation, useNavigate, useNavigationType } from "react-router"
import { motion, useReducedMotion } from "motion/react"
import { ChevronLeft } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import type { PublicShopProfile } from "@/lib/api/public-consumer"
import { ShopLogo } from "./shop-chrome"
import { useFlowHeadingFocus } from "./flow-chrome"
import type { FlowProgress } from "./flow-progress"
import type { Phase } from "./intake-flow-types"

// The frame around every light ("paper") screen of the consumer flow:
// Back · the shop · ONE progress bar with one label. Nothing else competes
// for attention. The shop's colour appears only on its logo.

export function FlowFrame({
  shop,
  phase,
  progress,
  onBack,
  status,
  focusKey,
  wide = false,
  children,
}: {
  shop: PublicShopProfile
  phase: Phase
  progress: FlowProgress
  onBack: (() => void) | null
  // Small live status on the right of the label row ("Reading your photos…").
  status?: ReactNode
  // Moves keyboard/screen-reader focus to the new screen's heading on change.
  focusKey: string
  // Two-column screens on a computer (the desktop handoff).
  wide?: boolean
  children: ReactNode
}) {
  useFlowHeadingFocus(focusKey)
  useHardwareBack(onBack)
  const reduce = useReducedMotion()

  return (
    <div data-phase={phase} className="min-h-[100dvh] bg-paper text-ink">
      <header className="sticky top-0 z-30 bg-paper/92 pt-[env(safe-area-inset-top)] backdrop-blur-md">
        <div className={cx("mx-auto px-4 md:px-6", wide ? "max-w-[1040px]" : "max-w-[560px]")}>
          <div className="flex h-14 items-center gap-2">
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                aria-label="Back"
                className="-ml-2 grid size-11 shrink-0 place-items-center rounded-full text-ink transition-colors duration-150 hover:bg-ink/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
              >
                <ChevronLeft className="size-6" aria-hidden />
              </button>
            ) : (
              <span className="-ml-2 size-11 shrink-0" aria-hidden />
            )}
            <div className="flex min-w-0 flex-1 items-center justify-center gap-2.5 pr-9">
              <ShopLogo shop={shop} />
              <span className="truncate text-[15px] font-semibold text-ink">{shop.name}</span>
            </div>
          </div>

          <div className="pb-3">
            <div
              role="progressbar"
              aria-label="Your progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress.value * 100)}
              aria-valuetext={progress.label}
              className="h-1 w-full overflow-hidden rounded-full bg-ink/10"
            >
              <motion.div
                className="h-full origin-left rounded-full bg-signal"
                initial={false}
                animate={{ scaleX: progress.value }}
                transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 260, damping: 32 }}
              />
            </div>
            <div className="mt-2 flex min-h-5 items-center justify-between gap-3">
              <p className="text-[13px] font-semibold text-zinc-600">{progress.label}</p>
              {status}
            </div>
          </div>
        </div>
      </header>

      <main
        className={cx(
          "mx-auto flex min-h-[calc(100dvh-7.5rem)] flex-col px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] md:px-6",
          wide ? "max-w-[1040px]" : "max-w-[560px]",
        )}
      >
        {children}
      </main>
    </div>
  )
}

// The phone's own Back (and the browser's) does what the on-screen Back does
// — close the camera, previous shot, previous question — instead of leaving
// the flow. While there's somewhere to go back to, one extra history entry
// (same URL) sits on top; popping it runs onBack and puts it back.
function useHardwareBack(onBack: (() => void) | null) {
  const navigate = useNavigate()
  const location = useLocation()
  const navType = useNavigationType()
  const onBackRef = useRef(onBack)
  useLayoutEffect(() => {
    onBackRef.current = onBack
  })
  const guarded = (location.state as { etxBack?: boolean } | null)?.etxBack === true
  const hasBack = !!onBack
  const pushing = useRef(false)

  useEffect(() => {
    if (!hasBack || guarded || pushing.current) return
    pushing.current = true
    navigate(`${location.pathname}${location.search}`, { state: { etxBack: true } })
  }, [hasBack, guarded, navigate, location.pathname, location.search])

  // The first page load also reads as a POP — only later moves count.
  const lastKey = useRef(location.key)
  useEffect(() => {
    if (location.key === lastKey.current) return
    lastKey.current = location.key
    if (guarded) {
      pushing.current = false
      return
    }
    // Landed below our entry by going back: that was a Back press.
    if (navType === "POP" && onBackRef.current) onBackRef.current()
  }, [location.key, guarded, navType])
}
